import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { fetchFixtures } from '../lib/api';
import type { FixtureSummary } from '../lib/types';
import FixtureCard from '../components/fixtures/FixtureCard';
import { FixtureCardSkeleton } from '../components/ui/Skeleton';
import EmptyState from '../components/ui/EmptyState';
import {
  TicketIcon,
  CheckCircleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import usePageTitle from '../hooks/usePageTitle';

export default function HomePage() {
  usePageTitle();

  const [fixtures, setFixtures] = useState<FixtureSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeLeague, setActiveLeague] = useState<string | null>(null);

  useEffect(() => {
    fetchFixtures()
      .then(setFixtures)
      .catch(() => setError('Failed to load fixtures'))
      .finally(() => setLoading(false));
  }, []);

  // Extract unique leagues
  const leagues = useMemo(() => {
    const seen = new Map<string, string>();
    for (const f of fixtures) {
      if (f.league_slug && f.league_name && !seen.has(f.league_slug)) {
        seen.set(f.league_slug, f.league_name);
      }
    }
    return Array.from(seen.entries()).map(([slug, name]) => ({ slug, name }));
  }, [fixtures]);

  // Fixtures where Ticketed wins (cheaper in at least 1 category, not more expensive in any)
  const ticketedWins = useMemo(() => {
    return fixtures.filter((f) => {
      const c = f.ticketed_cheaper || 0;
      const m = f.ticketed_more_expensive || 0;
      return c > 0 && m === 0;
    });
  }, [fixtures]);

  // Filter by league
  const filtered = useMemo(() => {
    if (!activeLeague) return fixtures;
    return fixtures.filter((f) => f.league_slug === activeLeague);
  }, [fixtures, activeLeague]);

  return (
    <div>
      {/* Hero section */}
      <div className="text-center mb-10">
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-100 mb-3">
          Compare Football Ticket Prices
        </h1>
        <p className="text-slate-400 text-lg max-w-2xl mx-auto">
          Find the best deals across multiple ticket suppliers for upcoming matches
        </p>
      </div>

      {/* Ticketed Wins highlight */}
      {!loading && ticketedWins.length > 0 && (
        <section className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-100">
              <CheckCircleIcon className="w-5 h-5 text-emerald-400" />
              Ticketed Best Prices
            </h2>
            <Link
              to="/insights"
              className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-emerald-400 transition-colors"
            >
              <ChartBarIcon className="w-3.5 h-3.5" />
              View all insights
            </Link>
          </div>
          <div className="grid gap-3">
            {ticketedWins.slice(0, 5).map((fixture) => (
              <FixtureCard key={fixture.match_id} fixture={fixture} />
            ))}
          </div>
          {ticketedWins.length > 5 && (
            <p className="text-center text-xs text-slate-500 mt-3">
              + {ticketedWins.length - 5} more matches where Ticketed has the best price
            </p>
          )}
        </section>
      )}

      {/* All Fixtures section */}
      <section>
        <h2 className="text-lg font-semibold text-slate-100 mb-4">
          All Upcoming Fixtures
        </h2>

        {/* League filter chips */}
        {!loading && leagues.length > 0 && (
          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 scrollbar-none">
            <button
              onClick={() => setActiveLeague(null)}
              className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeLeague === null
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-800 text-slate-400 border border-slate-700/50 hover:bg-slate-700 hover:text-slate-200'
              }`}
            >
              All Leagues
            </button>
            {leagues.map((league) => (
              <button
                key={league.slug}
                onClick={() => setActiveLeague(league.slug)}
                className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeLeague === league.slug
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-800 text-slate-400 border border-slate-700/50 hover:bg-slate-700 hover:text-slate-200'
                }`}
              >
                {league.name}
              </button>
            ))}
          </div>
        )}

        {/* Fixture list */}
        {loading ? (
          <div className="grid gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <FixtureCardSkeleton key={i} />
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon={<TicketIcon className="w-7 h-7" />}
            title="Something went wrong"
            description={error}
            actionLabel="Try again"
            actionTo="/"
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<TicketIcon className="w-7 h-7" />}
            title="No fixtures found"
            description={activeLeague
              ? 'No upcoming fixtures with price comparisons in this league.'
              : 'No upcoming fixtures with price comparisons available.'}
            actionLabel={activeLeague ? 'Show all leagues' : undefined}
            actionTo={activeLeague ? '/' : undefined}
          />
        ) : (
          <div className="grid gap-3">
            {filtered.map((fixture) => (
              <FixtureCard key={fixture.match_id} fixture={fixture} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
